import { type ReactNode, useEffect } from 'react';

import {
  AudioRecorderProvider,
  ChunkUploadProvider,
  MicrophoneProvider,
  registerUploadWorker,
} from '@repo/videa-notes';
import type { RootStateOrAny } from 'react-redux';
import { useSelector } from 'react-redux';

import { usePreferences } from '../../../react-query/usePreferences';

// Module-level flag to ensure SW registration only happens once
let swRegistrationAttempted = false;

interface VideaNotesAudioRecorderProviderProps {
  children: ReactNode;
}

/**
 * Wrapper component that reads feature flags from preferences
 * and passes them to the AudioRecorderProvider.
 */
export const VideaNotesAudioRecorderProvider = ({
  children,
}: VideaNotesAudioRecorderProviderProps) => {
  const user = useSelector((state: RootStateOrAny) => state?.login?.user);
  const currentPracticeId = user?.primaryPracticeId;
  const currentOrganizationId = user?.primaryOrganizationId;

  const { data: preferences } = usePreferences();

  // Feature flags for async upload - read from preferences
  const enableLocalAsyncUpload =
    preferences?.gateNotesLocalAsyncUpload ?? false;
  const enableSynapseAsyncUpload =
    preferences?.gateNotesSynapseAsyncUpload ?? false;

  // Register the upload service worker once on first mount
  useEffect(() => {
    if (swRegistrationAttempted) return;
    swRegistrationAttempted = true;

    registerUploadWorker().catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[VideaNotes] Failed to register upload worker:', err);
    });
  }, []);

  return (
    <MicrophoneProvider>
      <ChunkUploadProvider>
        <AudioRecorderProvider
          organizationId={currentOrganizationId}
          practiceId={currentPracticeId}
          enableLocalAsyncUpload={enableLocalAsyncUpload}
          enableSynapseAsyncUpload={enableSynapseAsyncUpload}
        >
          {children}
        </AudioRecorderProvider>
      </ChunkUploadProvider>
    </MicrophoneProvider>
  );
};
